import React, { useCallback, useEffect, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// Hooks
import { useSelectClient } from "@atoms/SelectClient";
import { useAuth } from "src/hooks/useAuth";

// Atoms
import { DashboardWebLink, Loading } from "@atoms/index";

// Molecules
import { BaseCard, BaseCardItem, BaseCardHeader } from "@molecules/index";

// Constants
import { ModulesID } from "src/store/constants";

// Assets
import { contract } from "@assets/applications/index";

// graphql
import {
  TContractAllWithPaginationQueries,
  contractAllWithPagination
} from "@graphql/groups/contract/query.graphql";
import {
  ContractQueriesGetAllWithPaginationArgs,
  ContractType,
  ECommandResultStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";

import useTranslation from "@languages/useTranslation";
import { GlobalMessages } from "@languages/interfaces";
import * as Styled from "./styles";

const DashboardWebContracts: React.FC = () => {
  const { t } = useTranslation();
  const { value: selectedClient } = useSelectClient();
  const [whereContractIds, setWhereContractIds] = useState<string>("");
  const [contracts, setContracts] = useState<ContractType[]>([]);

  const { addToast } = useToast();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  const [
    getPermissions,
    {
      loading: loadingPermissions,
      error: errorPermissions,
      data: dataPermissions
    }
  ] = useLazyQuery<
  TPermissionAllWithoutPaginationQueries,
  PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.id"]), {
    fetchPolicy: "network-only",
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "construction.client.id",
          operator: EWhereClausesOperators.EQUALS,
          value: selectedClient.id
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.CONTRACT
        }
      ]
    }
  });

  const [
    getContracts,
    { loading: loadingContracts, error: errorContracts, data: dataContracts }
  ] = useLazyQuery<
    TContractAllWithPaginationQueries,
    ContractQueriesGetAllWithPaginationArgs
  >(
    contractAllWithPagination([
      "id",
      "status",
      "code",
      "contractDate",
      "contractConcreteRecipes.id",
      "contractConcreteRecipes.status",
      "contractConcreteRecipes.m3Value",
      "contractConcreteRecipes.totalVolume",
      "contractConcreteRecipes.deliveredVolume",
      "contractConcreteRecipes.concreteRecipe.description",
      "construction.name",
      "construction.address",
      "construction.county",
      "construction.number",
      "construction.complement",
      "contractSellers.seller.name"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );

  const loadContracts = useCallback(async () => {
    const response = dataContracts?.contract.getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const contractsSorted = response.result
        ?.sort((a, b) => (a?.contractDate < b?.contractDate ? 1 : -1))
        .slice(0, 5);

      setContracts(contractsSorted as ContractType[]);
    }
  }, [dataContracts, setContracts]);

  const makeWhereContractIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const contractIds: string[] = [];

      response.result?.forEach((permission) => {
        contractIds.push(permission?.contract?.id || "");
      });

      const contractIdsStr = contractIds
        .filter((id) => id && id !== "")
        .join("|");

      if (!contractIdsStr || contractIdsStr === "") {
        setWhereContractIds("");
        setContracts([]);

        return;
      }

      setWhereContractIds(contractIdsStr);
    }
  }, [dataPermissions]);

  useEffect(() => {
    if (whereContractIds !== "") {
      getContracts({
        variables: {
          where: [
            {
              property: "id",
              operator: EWhereClausesOperators.IN,
              value: whereContractIds
            }
          ]
        }
      });
    }
  }, [getContracts, whereContractIds]);

  useEffect(() => {
    if (dataPermissions) makeWhereContractIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereContractIds]);

  useEffect(() => {
    if (dataContracts) loadContracts();
    if (errorContracts) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorContracts.message
      });
    }
  }, [addToast, dataContracts, errorContracts, loadContracts]);

  useEffect(() => {
    setContracts([]);
  }, [selectedClient]);

  const detailsContract = useCallback((contract: ContractType) => {
    if (!contract.contractConcreteRecipes) return undefined;

    return (
      <div key="teste">
        {contract.contractConcreteRecipes.map((contractConcreteRecipe) => {
          if (!contractConcreteRecipe) return undefined;

          return (
            <BaseCardItem
              key={contractConcreteRecipe.id}
              type="contractConcreteRecipe"
              contractConcreteRecipe={contractConcreteRecipe}
            />
          );
        })}
      </div>
    );
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWhereContractIds("");
      getPermissions();
    }, 600000);

    return () => {
      clearInterval(interval);
    };
  }, [getPermissions]);

  useEffect(() => {
    getPermissions();
  }, [getPermissions]);

  return (
    <Styled.Container>
      <DashboardWebLink
        icon={contract}
        title={t(GlobalMessages.lastContractsText)}
        link="/contracts"
      />

      <Styled.Content>
        {(loadingContracts || loadingPermissions) && <Loading loading />}
        {!loadingPermissions
          && !loadingContracts
          && contracts
          && contracts.map((contract) => (
            <BaseCard
              key={contract.id}
              id={contract.id}
              header={<BaseCardHeader type="contract" data={contract} />}
              details={detailsContract(contract)}
            />
          ))}
      </Styled.Content>
    </Styled.Container>
  );
};

export default DashboardWebContracts;
